<template>
    <div class="vgtc-hero">
        <OffCanvasMenu :ref="offCanvasMenuRef" />
        <Header class="d-none d-lg-block" :hideLogo="true" />
        <div class="vgtc-hero__content">
            <div class="vgtc-hero__header">
                <HamburgerMenu @toggle="toggleSlideMenu" class="d-md-none vgtc-hero__hamburger" />
                <div class="vgtc-hero__header-text">
                    <span class="vgtc-hero__header--bold">{{ headerTitleParts.first }}</span>
                    <span class="vgtc-hero__header--rest">{{ headerTitleParts.rest }}</span>
                </div>
            </div>

            <div class="vgtc-hero__cards">
                <div class="vgtc-hero__card vgtc-hero__card--word">
                    <div class="vgtc-hero__card-title d-flex align-items-center">
                        <span class="vgtc-hero__card-title-icon"><font-awesome-icon :icon="['far','text']" /></span>
                        <span class="ml-2">Zoek op woord</span>
                    </div>
                    <div class="vgtc-hero__card-subtitle">Typ een woord en ontdek het gebaar.</div>
                    <div class="d-flex align-items-center justify-content-center mt-3">
                        <SearchBar :showPrefix="false" class="flex-grow-1 vgtc-hero__searchbar" />
                    </div>
                </div>

                <div class="vgtc-hero__card vgtc-hero__card--gesture">
                    <div class="vgtc-hero__card-title d-flex align-items-center">
                        <span class="vgtc-hero__card-title-icon">
                            <font-awesome-icon :icon="['far','video']" />
                        </span>
                        <span class="ml-2">Zoek op gebaar</span>
                    </div>
                    <div class="vgtc-hero__card-subtitle">Toon een gebaar en ontdek de betekenis.</div>
                    <div class="d-flex flex-column align-items-center justify-content-center signbuddy mt-3">
                        <router-link to="/direct-record" class="vgtc-button d-flex align-items-center vgtc-hero__camera-button">
                            <font-awesome-icon :icon="['far','camera']" class="icon" />
                            Open camera
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Header from '../Header.vue';
import OffCanvasMenu from '../OffCanvasMenu.vue';
import SearchBar from './SearchBar.vue';
import HamburgerMenu from '../general/HamburgerMenu.vue';

const search = namespace('search');

@Component({
    components: { Header, OffCanvasMenu, SearchBar, HamburgerMenu },
})
export default class TopNavbar extends Vue {
    @search.Getter public userIsSearching!: boolean;

    private offCanvasMenuRef = 'off-canvas-menu';

    public toggleSlideMenu(e: any): void {
        // Way to prevent the mouseclick on the button to trigger the
        // "outside-menu-clicked" state which would close the menu.
        e.stopPropagation();
        (this.$refs[this.offCanvasMenuRef] as OffCanvasMenu).toggleSlideMenu();
    }

    get headerTitleParts(): { first: string; rest: string } {
        const title = (this.$t('header.vgtc_dutch') as unknown as string) || '';
        const [first, ...rest] = title.split(' ');
        return { first: first || '', rest: rest.join(' ') };
    }
}
</script>
<style lang="scss" scoped>
.signbuddy {
    color: $header-text-color;
    font-size: $header-text-size;
    font-family: $font-family-accent;
    font-weight: 700;
    text-align: center;
}

.icon {
    height: 1.375rem;
}

.vgtc-hero {
    background-image: url('../../../public/img/vgtc-banner.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    position: relative;
    z-index: $zindex-dropdown;
}

.vgtc-button {
    gap: 0.625rem;
    text-transform: unset;
}

.vgtc-hero.navbar {
    background-color: $header-color;
    box-shadow: $box-shadow-navbar;
}

.vgtc-hero__content {
    display: grid;
    justify-items: center;

    // Reduce top spacing, increase bottom spacing
    @include media-breakpoint-down(lg) {
        padding: 1.5rem 0 3rem; // top, horizontal, bottom
    }

    @include media-breakpoint-up(lg) {
        padding: 2rem 0 5rem; // top, horizontal, bottom
    }
}

.vgtc-hero__header {
    color: $header-text-color;
    // Scale title smoothly with viewport. Min 20px on smallest screens, max 32px on large.
    font-size: clamp(1.25rem, 1rem + 1.5vw, 2rem);
    font-family: $font-family-accent;
    text-align: center;
    font-weight: bold;
    margin-bottom: 2.25rem; // add a bit more space to boxes
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    &--rest {
        font-weight: 400;
    }
}

.vgtc-hero__header--bold {
    font-weight: 700;
}

.vgtc-hero__hamburger {
    position: absolute;
    left: 1rem;
}

.vgtc-hero__header-text {
    display: inline-flex;
    gap: 0.25rem;
    align-items: baseline;

    // Stack on small screens
    @include media-breakpoint-down(md) {
        display: inline-grid;
        grid-auto-flow: row;
        justify-items: center;
        line-height: 1.1;
    }

    // Ensure exact 20px on the smallest breakpoint for both lines, with bold/regular weights
    @include media-breakpoint-down(sm) {
        .vgtc-hero__header--bold,
        .vgtc-hero__header--rest {
            font-size: 20px; // 1.25rem
            font-family: $font-family-accent; // Raleway
        }
        .vgtc-hero__header--bold {
            font-weight: 700;
        }
        .vgtc-hero__header--rest {
            font-weight: 400;
        }
    }
}

.vgtc-hero__cards {
    display: grid;
    gap: 2rem;
    width: 100%;
    padding: 0 1rem;

    @include media-breakpoint-down(md) {
        gap: 1.25rem;
        padding: 0 0.75rem;
        grid-template-areas:
            'gesture'
            'word';
    }

    @include media-breakpoint-up(lg) {
        grid-template-columns: repeat(2, 25rem);
        column-gap: 2rem; // 32px between cards
        justify-content: center;
        max-width: 52rem; // 25rem + 2rem + 25rem
    }
}

.vgtc-hero__card {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 2rem 1.5rem;
    backdrop-filter: blur(4px);
    text-align: center;
    width: 100%;
    max-width: 36rem; // limit width when stacked
    margin-left: auto;
    margin-right: auto;

    @include media-breakpoint-down(md) {
        padding: 1.25rem 1rem;
    }

    // On very small screens, make the card a bit narrower than the viewport
    @include media-breakpoint-down(sm) {
        width: 90%;
    }

    @include media-breakpoint-up(lg) {
        width: 25rem; // 400px
        justify-self: center;
    }
}

.vgtc-hero__card--word {
    @include media-breakpoint-down(md) {
        grid-area: word;
    }
}

.vgtc-hero__card--gesture {
    @include media-breakpoint-down(md) {
        grid-area: gesture;
    }
}

.vgtc-hero__card-title {
    color: $header-text-color;
    // Raleway Bold, responsive with 16px minimum
    font-size: clamp(1rem, 0.9rem + 0.7vw, 1.25rem);
    font-family: $font-family-accent;
    font-weight: 700;
    justify-content: center;
    margin-bottom: 2rem;

    @include media-breakpoint-down(md) {
        margin-bottom: 1rem;
    }
}

.vgtc-hero__card-title-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    line-height: 1;
    background: transparent;
    width: auto;
    height: auto;
    border-radius: 0;
    font-family: $font-family-serif;
}

.vgtc-hero__card-subtitle {
    color: $header-text-color;
    opacity: 0.85;
    // Roboto Regular, responsive with 12px minimum
    font-family: $font-family;
    font-weight: 400;
    font-size: clamp(0.75rem, 0.7rem + 0.3vw, 0.875rem);
    margin-top: 0.5rem;
}

.vgtc-hero__camera-button {
    text-transform: unset;
    // Roboto SemiBold, responsive with 14px minimum
    font-family: $font-family;
    font-weight: 600;
    font-size: clamp(0.875rem, 0.85rem + 0.2vw, 0.9375rem);
}

.vgtc-hero__content__text {
    color: $header-text-color;
    font-size: 2rem;
    font-family: $font-family-accent;
    text-align: center;
    font-weight: bold;

    @include media-breakpoint-down(lg) {
        font-size: calc(0.35rem + 3vw);
    }
}

.vgtc-hero__searchbar {
    width: 100%;
    max-width: 30rem;

    @include media-breakpoint-down(sm) {
        max-width: 22rem;
    }

    @include media-breakpoint-up(lg) {
        max-width: 40rem;
    }
}
</style>
